import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useTheme } from '@/context/ThemeContext';
import PageSEO from '@/components/PageSEO';
import { ArrowLeft, Sparkles, KeyRound, Loader2, Trash2, ShieldCheck } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const req = { withCredentials: true };

export default function IntelligenceSettingsPage() {
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const [providers, setProviders] = useState([]);
  const [config, setConfig] = useState(null);
  const [provider, setProvider] = useState('emergent');
  const [model, setModel] = useState('');
  const [models, setModels] = useState([]);
  const [apiKey, setApiKey] = useState('');
  const [baseUrl, setBaseUrl] = useState('');
  const [budget, setBudget] = useState(1200);
  const [saving, setSaving] = useState(false);

  const T = {
    bg: isDark ? 'bg-zinc-950 text-zinc-100' : 'bg-zinc-50 text-zinc-900',
    card: isDark ? 'border-zinc-800 bg-zinc-900' : 'border-zinc-200 bg-white',
    muted: isDark ? 'text-zinc-400' : 'text-zinc-600',
    dim: isDark ? 'text-zinc-600' : 'text-zinc-400',
    input: isDark ? 'bg-zinc-950 border-zinc-800 text-zinc-200' : 'bg-white border-zinc-200 text-zinc-800',
  };

  const load = useCallback(async () => {
    const [{ data: p }, { data: c }] = await Promise.all([
      axios.get(`${API}/api/intelligence/providers`, req),
      axios.get(`${API}/api/intelligence/config`, req),
    ]);
    setProviders(p); setConfig(c);
    setProvider(c.provider); setModel(c.model);
    setBaseUrl(c.base_url || ''); setBudget(c.max_tokens_per_run);
  }, []);

  useEffect(() => { load().catch(() => toast.error('Could not load your model settings')); }, [load]);

  useEffect(() => {
    if (!provider) return;
    axios.get(`${API}/api/intelligence/models?provider=${provider}`, req)
      .then(({ data }) => {
        setModels(data.models);
        setModel(m => (data.models.includes(m) ? m : data.models[0] || ''));
      })
      .catch(() => setModels([]));
  }, [provider]);

  const spec = providers.find(p => p.id === provider);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/api/intelligence/config`, {
        provider, model, api_key: apiKey || null, base_url: baseUrl, max_tokens_per_run: Number(budget),
      }, req);
      setConfig(data); setApiKey('');
      toast.success('Saved', { description: `${data.provider} · ${data.model} is now answering questions.` });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error('Could not save', { description: typeof d === 'string' ? d : 'Check the key and try again.' });
    } finally { setSaving(false); }
  };

  const removeKey = async () => {
    if (!window.confirm('Delete the stored key? The workspace falls back to the built-in model.')) return;
    await axios.delete(`${API}/api/intelligence/config`, req);
    toast.success('Key deleted');
    setApiKey('');
    await load();
  };

  return (
    <div className={`min-h-screen ${T.bg}`}>
      <PageSEO title="Intelligence Settings" description="Bring your own LLM key — OpenRouter, OpenAI, Anthropic, Gemini or a self-hosted open-source endpoint." />
      <main id="main" className="max-w-3xl mx-auto px-4 sm:px-8 py-12">
        <button data-testid="settings-back" onClick={() => navigate('/dashboard')}
          className={`text-xs inline-flex items-center gap-1 mb-8 ${T.dim} hover:underline`}>
          <ArrowLeft size={13} /> Back to dashboard
        </button>

        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={20} style={{ color: '#00D4FF' }} />
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">Intelligence</h1>
        </div>
        <p className={`text-base max-w-2xl mb-10 leading-relaxed ${T.muted}`}>
          Choose which model answers questions about your numbers. Bring a paid key, an OpenRouter key
          for open-source models, or point at your own self-hosted endpoint. Keys are encrypted at rest
          and never sent back to the browser.
        </p>

        {config && (
          <div data-testid="intel-usage" className={`rounded-xl border p-5 mb-8 ${T.card}`}>
            <p className="text-sm font-bold mb-1">
              Currently: {config.provider} · {config.model}
              {config.has_key && <span className={`ml-2 text-xs font-mono ${T.dim}`}>key {config.key_hint}</span>}
            </p>
            <p className={`text-xs ${T.muted}`}>
              This month: {config.usage_this_month.runs} question(s) · ~{config.usage_this_month.tokens_estimated.toLocaleString()} tokens
              {' '}· cap {config.max_tokens_per_run} tokens per answer
            </p>
          </div>
        )}

        <form onSubmit={save} className={`rounded-xl border p-6 sm:p-8 space-y-6 ${T.card}`}>
          <div>
            <label className="block text-xs font-bold uppercase tracking-wider mb-3">Provider</label>
            <div className="grid sm:grid-cols-2 gap-3">
              {providers.map(p => (
                <button key={p.id} type="button" data-testid={`provider-opt-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`text-left p-4 rounded-lg border transition-all ${provider === p.id
                    ? 'border-blue-600 ring-1 ring-blue-600/40'
                    : isDark ? 'border-zinc-800 hover:border-zinc-700' : 'border-zinc-200 hover:border-zinc-300'}`}>
                  <span className="block text-sm font-bold">{p.label}</span>
                  <span className={`block text-xs mt-1 ${T.muted}`}>{p.note}</span>
                  {!p.needs_key && (
                    <span className="inline-flex items-center gap-1 text-[10px] font-bold mt-2" style={{ color: '#06D6A0' }}>
                      <ShieldCheck size={11} /> NO KEY NEEDED
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>

          {provider === 'custom' && (
            <div>
              <label htmlFor="base-url" className="block text-xs font-bold uppercase tracking-wider mb-2">Endpoint base URL</label>
              <input id="base-url" data-testid="intel-base-url" value={baseUrl} onChange={e => setBaseUrl(e.target.value)}
                placeholder="https://my-vllm.example.com/v1"
                className={`w-full px-3 py-2 rounded-md text-sm border outline-none font-mono ${T.input}`} />
              <p className={`text-xs mt-2 ${T.dim}`}>Anything OpenAI-compatible: vLLM, Ollama, LM Studio, LiteLLM.</p>
            </div>
          )}

          {spec?.needs_key && (
            <div>
              <label htmlFor="api-key" className="block text-xs font-bold uppercase tracking-wider mb-2">
                API key {config?.has_key && config.provider === provider && <span className={`font-normal ${T.dim}`}>— stored, leave blank to keep</span>}
              </label>
              <div className="relative">
                <KeyRound size={14} className={`absolute left-3 top-1/2 -translate-y-1/2 ${T.dim}`} />
                <input id="api-key" data-testid="intel-api-key" type="password" value={apiKey} autoComplete="off"
                  onChange={e => setApiKey(e.target.value)} placeholder="sk-…"
                  className={`w-full pl-9 pr-3 py-2 rounded-md text-sm border outline-none font-mono ${T.input}`} />
              </div>
            </div>
          )}

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="model" className="block text-xs font-bold uppercase tracking-wider mb-2">Model</label>
              {models.length ? (
                <select id="model" data-testid="intel-model" value={model} onChange={e => setModel(e.target.value)}
                  className={`w-full px-3 py-2 rounded-md text-sm border outline-none ${T.input}`}>
                  {models.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              ) : (
                <input id="model" data-testid="intel-model-free" value={model} onChange={e => setModel(e.target.value)}
                  placeholder="e.g. qwen2.5-72b-instruct"
                  className={`w-full px-3 py-2 rounded-md text-sm border outline-none font-mono ${T.input}`} />
              )}
            </div>
            <div>
              <label htmlFor="budget" className="block text-xs font-bold uppercase tracking-wider mb-2">Max tokens per answer</label>
              <input id="budget" data-testid="intel-budget" type="number" min={200} max={4000} step={100}
                value={budget} onChange={e => setBudget(e.target.value)}
                className={`w-full px-3 py-2 rounded-md text-sm border outline-none font-mono ${T.input}`} />
              <p className={`text-xs mt-2 ${T.dim}`}>Keeps cost predictable. 200–4000.</p>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 pt-2">
            <button data-testid="intel-save" type="submit" disabled={saving}
              className="px-6 py-2.5 rounded-md text-sm font-bold text-white disabled:opacity-60"
              style={{ background: 'linear-gradient(135deg,#002FA7,#00D4FF)' }}>
              {saving ? <Loader2 size={15} className="animate-spin" /> : 'Save and validate'}
            </button>
            {config?.has_key && (
              <button data-testid="intel-delete-key" type="button" onClick={removeKey}
                className="px-4 py-2.5 rounded-md text-sm font-bold inline-flex items-center gap-1"
                style={{ background: 'rgba(255,107,107,.1)', color: '#FF6B6B' }}>
                <Trash2 size={13} /> Delete stored key
              </button>
            )}
          </div>
        </form>

        <p className={`text-xs mt-6 leading-relaxed ${T.dim}`}>
          The analyst only ever sees the figures already on your dashboard, and is instructed to refuse
          rather than invent a number it does not have.
        </p>
      </main>
    </div>
  );
}
